// src/App.js
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import './App.css';
import MyHeader from './comps/MyHeader';
import MyMenu from './comps/MyMenu';
import AppRoutes from './AppRoutes';
import MyFooter from './comps/MyFooter';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AutoLogout from './comps/AutoLogout';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';

// Service worker registration helper (if you have it)
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";

function App() {
  const location = useLocation();

  // Determine header/menu display
  const pathname = location?.pathname || window.location.pathname;
  const lastSegment = pathname.replace(/\/+$/, "").split("/").pop() || "";
  const hasSession = !!sessionStorage.getItem("ldata");
  const publicRoutes = new Set(["login", "lab_report"]);
  const isPublic = publicRoutes.has(lastSegment.toLowerCase());
  const isLogin = !isPublic && hasSession;

  // network
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSlow, setIsSlow] = useState(false);

  // PWA update states
  const [updateReady, setUpdateReady] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);

  // ---------- SERVICE WORKER DETECTION & REGISTRATION ----------
  useEffect(() => {
    console.log("[App] mount: checking service worker support:", 'serviceWorker' in navigator);

    // helper to mark update available
    const setUpdate = (worker, source = "unknown") => {
      console.log(`[App] update detected from ${source}. waitingWorker:`, worker);
      setWaitingWorker(worker || null);
      setUpdateReady(true);
      toast.info("⬆️ New app version available — click Update in header");
    };

    // 1) Try to use your serviceWorkerRegistration (if exists)
    try {
      if (serviceWorkerRegistration && typeof serviceWorkerRegistration.register === "function") {
        console.log("[App] calling serviceWorkerRegistration.register()");
        serviceWorkerRegistration.register({
          onUpdate: (registration) => {
            console.log("[App] serviceWorkerRegistration.onUpdate fired:", registration);
            setUpdate(registration.waiting, "serviceWorkerRegistration.onUpdate");
          },
          onSuccess: (reg) => {
            console.log("[App] serviceWorkerRegistration.onSuccess", reg);
          },
        });
      } else {
        console.warn("[App] serviceWorkerRegistration.register not found");
      }
    } catch (err) {
      console.warn("[App] serviceWorkerRegistration.register threw:", err);
    }

    // 2) Immediate manual check: is there a registration with waiting worker?
    const checkWaiting = async () => {
      try {
        if (!('serviceWorker' in navigator)) return;
        const reg = await navigator.serviceWorker.getRegistration();
        console.log("[App] getRegistration()", reg);
        if (reg && reg.waiting) {
          setUpdate(reg.waiting, "navigator.getRegistration");
        }
      } catch (err) {
        console.warn("[App] error checking registration:", err);
      }
    };

    // 3) Listen for installing/updated registration changes via navigator.serviceWorker events
    const onRegistrationUpdate = (event) => {
      // Not all browsers fire this; keep for completeness
      console.log("[App] navigator.serviceWorker event:", event);
    };

    navigator.serviceWorker && navigator.serviceWorker.addEventListener && navigator.serviceWorker.addEventListener("message", (ev) => {
      console.log("[App] SW message event:", ev);
    });

    // 4) Poll briefly for waiting worker — helps when SW installs shortly after page load.
    let pollInterval = null;
    (async () => {
      await checkWaiting();
      let tries = 0;
      pollInterval = setInterval(async () => {
        tries++;
        await checkWaiting();
        if (tries >= 12) {
          clearInterval(pollInterval);
          pollInterval = null;
        }
      }, 3000); // check every 3s for up to ~36s
    })();

    // Cleanup
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh handler: message the waiting worker to skipWaiting then reload when controller changes
  const refreshApp = () => {
    console.log("[App] refreshApp called. waitingWorker:", waitingWorker);

    if (!waitingWorker) {
      console.warn("[App] No waiting worker found, force reload.");
      window.location.reload(true);
      return;
    }

    const onControllerChange = () => {
      console.log("[App] controllerchange event — reloading page to activate new SW.");
      window.location.reload();
    };

    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

    try {
      waitingWorker.postMessage({ type: "SKIP_WAITING" });
      // safety: remove listener after 8s
      setTimeout(() => {
        try {
          navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
        } catch (e) {}
      }, 8000);
    } catch (err) {
      console.error("[App] error posting SKIP_WAITING to worker:", err);
      window.location.reload(true);
    }
  };

  // ---------- NETWORK CONNECTIVITY ---------- (unchanged, but with logs)
  useEffect(() => {
    let isMounted = true;
    const handleOnline = () => {
      if (!isMounted) return;
      setIsOnline(true);
      setIsSlow(false);
      toast.success("✅ Internet connection restored");
      console.log("[App] online");
    };
    const handleOffline = () => {
      if (!isMounted) return;
      setIsOnline(false);
      setIsSlow(false);
      toast.error("❌ No Internet connection");
      console.log("[App] offline");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    const checkSpeed = async () => {
      if (!navigator.onLine) {
        if (!isMounted) return;
        setIsOnline(false);
        setIsSlow(false);
        return;
      }
      const start = performance.now();
      try {
        await fetch("/favicon.ico", { cache: "no-cache", method: "GET" });
        const duration = performance.now() - start;
        if (!isMounted) return;
        if (duration > 2000) {
          if (!isSlow) toast.warning("⚠️ Slow Internet connection");
          setIsSlow(true);
          console.log("[App] slow connection:", Math.round(duration));
        } else {
          setIsSlow(false);
          console.log("[App] connection OK:", Math.round(duration));
        }
      } catch (err) {
        if (!isMounted) return;
        setIsOnline(false);
        setIsSlow(false);
        toast.error("❌ No Internet connection");
        console.warn("[App] checkSpeed fetch failed:", err);
      }
    };

    checkSpeed();
    const interval = setInterval(checkSpeed, 10000);

    return () => {
      isMounted = false;
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // mobile layout fix (unchanged)
  useEffect(() => {
    const fixMobileSpacing = () => {
      if (window.innerWidth < 992) {
        document.body.style.marginLeft = '0';
        document.body.style.paddingLeft = '0';
      }
    };

    fixMobileSpacing();
    window.addEventListener('resize', fixMobileSpacing);

    return () => {
      window.removeEventListener('resize', fixMobileSpacing);
    };
  }, []);

  const bannerStyleBase = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 9998,
    textAlign: "center",
    padding: "8px 12px",
    fontWeight: "600",
  };

  const topPadding = !isOnline || (isOnline && isSlow) ? "48px" : "16px";

  const getMainContentStyle = () => {
    const isMobile = window.innerWidth < 992;
    if (isMobile) {
      return {
        marginLeft: "0px",
        paddingTop: topPadding
      };
    }
    return {
      paddingTop: topPadding
    };
  };

  return (
    <div id="layout-wrapper">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        pauseOnHover
        draggable
      />

      {!isOnline && (
        <div style={{ ...bannerStyleBase, background: "#d9534f", color: "#fff" }}>
          🚫 No Internet Connection
        </div>
      )}

      {isOnline && isSlow && (
        <div style={{ ...bannerStyleBase, background: "#f0ad4e", color: "#fff" }}>
          ⚠️ Slow Internet Connection
        </div>
      )}

      <AutoLogout timeoutMs={2 * 60 * 60 * 1000} redirectPath="/login" />

      {isLogin ? <MyHeader updateReady={updateReady} refreshApp={refreshApp} /> : null}
      {isLogin ? <MyMenu /> : null}

      <div className="vertical-overlay"></div>

      <div className="main-content" style={getMainContentStyle()}>
        <AppRoutes />
        {isLogin ? <MyFooter /> : null}
      </div>
    </div>
  );
}

export default App;
